import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Landing Hero — big, calm, full-bleed photo of Cambados.
 * Simplified per client cue ("como para niños"): fewer words, larger CTAs.
 */
export default function Hero() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const reduced = useReducedMotion();
  const spring = reduced ? { duration: 0 } : { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

  return (
    <section
      className="relative min-h-[92vh] w-full overflow-hidden bg-salnes-ink"
      data-testid="landing-hero"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/brand/hero-salnes.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center 55%",
        }}
      />
      {/* Legibility overlay: darker bottom + soft left gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-salnes-ink/55 via-salnes-ink/30 to-salnes-ink/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-salnes-ink/60 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-40 pb-24 min-h-[92vh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={spring}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-salnes-sand mb-6">
            <span className="w-6 h-px bg-salnes-sand/70" />
            {t("hero.eyebrow")}
          </div>
          <h1
            data-testid="hero-title"
            className="font-serif text-white leading-[0.98] text-5xl sm:text-6xl lg:text-7xl xl:text-[92px]"
          >
            {lang === "es" ? "Salnés" : "Salnés"}
            <span className="block text-salnes-emerald/95">
              {lang === "es" ? "Sostenible" : "Sostible"}
            </span>
          </h1>
          <p className="mt-8 text-white/90 text-xl sm:text-2xl max-w-xl leading-snug font-light">
            {t("hero.subtitle")}
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href="#kpis-wow"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-salnes-emerald text-white px-8 h-14 text-base font-medium hover:bg-salnes-emerald/90 shadow-lg shadow-salnes-emerald/30 transition-all duration-200 hover:-translate-y-0.5"
            >
              {lang === "es" ? "Ver impacto en directo" : "Ver impacto en directo"}
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <Link
              to="/impacto"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-8 h-14 text-base font-medium hover:bg-white/10 transition-colors"
            >
              {lang === "es" ? "Portal de impacto" : "Portal de impacto"}
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: reduced ? 0 : 1.2, duration: 0.6 }}
          className="hidden md:flex items-center gap-3 text-white/60 text-[11px] uppercase tracking-[0.24em]"
        >
          <ChevronDown size={14} className={reduced ? "" : "animate-bounce"} />
          {t("hero.scroll")}
        </motion.div>
      </div>
    </section>
  );
}
